import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

// Eski bir service worker, yeni deploy sonrası artık var olmayan bir chunk'ı önbellekten
// servis etmeye çalışırsa bu fonksiyon SW kaydını ve tüm Cache Storage girişlerini temizler —
// aksi halde sayfa yenilense bile aynı bozuk önbellek tekrar servis edilebilir.
async function clearStaleServiceWorkerCache(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (err) {
    console.warn('[ErrorBoundary] Service worker/cache temizliği başarısız:', err);
  }
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[React Error Boundary]:', error, errorInfo);

    // Yeni sürüm yayınlandığında eski JS chunk'ı 404 verdiğinde otomatik 1 kere sayfa yenile
    if (
      error.message?.includes('dynamically imported module') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('ChunkLoadError')
    ) {
      const hasReloaded = sessionStorage.getItem('chunk_reload_done');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload_done', 'true');
        // Sadece reload yetmez: eski service worker hâlâ eski index.html/chunk'ları önbellekten
        // servis edebiliyor. Önce SW'yi ve cache'leri temizleyip öyle yeniliyoruz.
        void clearStaleServiceWorkerCache().finally(() => window.location.reload());
      }
    }
  }

  public handleReset = () => {
    sessionStorage.removeItem('chunk_reload_done');
    this.setState({ hasError: false, error: null });
    void clearStaleServiceWorkerCache().finally(() => {
      window.location.href = '/';
    });
  };

  public handleReload = () => {
    sessionStorage.removeItem('chunk_reload_done');
    this.setState({ hasError: false, error: null });
    void clearStaleServiceWorkerCache().finally(() => window.location.reload());
  };

  public render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.includes('dynamically imported module') ||
        this.state.error?.message?.includes('Failed to fetch dynamically imported module');

      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-2xl text-center space-y-5">
            <div className="w-16 h-16 bg-rose-50 dark:bg-rose-950/40 text-rose-600 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
              <AlertCircle size={32} />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {isChunkError ? 'Yeni Sürüm Yayınlandı' : 'Bir Hata Oluştu'}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                {isChunkError
                  ? 'Sitede yeni bir güncelleme yayınlandığı için önbellekteki dinamik dosya yenilendi. Lütfen "Sayfayı Yenile" butonuna tıklayın.'
                  : 'Sayfa yüklenirken beklenmeyen bir hata meydana geldi.'}
              </p>
            </div>

            {this.state.error && (
              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-xl text-left overflow-x-auto max-h-32">
                <code className="text-[11px] font-mono text-rose-500 dark:text-rose-400 break-all">
                  {this.state.error.message}
                </code>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-md transition-all active:scale-95"
              >
                <RefreshCw size={14} /> Yeniden Dene
              </button>
              <button
                onClick={this.handleReset}
                className="flex-1 py-2.5 px-4 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold rounded-xl text-xs flex items-center justify-center gap-2 border border-slate-200 dark:border-slate-700 transition-all active:scale-95"
              >
                <Home size={14} /> Anasayfa
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
